import React, { useEffect, useState } from "react";
import { Plus, Save, Sparkles, Loader2, Bold, Italic, Underline, FileText } from "lucide-react";
import { docsApi, aiQuick } from "../api";

const KIND = "word";

export default function Word() {
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");

  const load = async () => {
    const list = await docsApi.list(KIND);
    setDocs(list);
    if (!active && list[0]) setActive(list[0]);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!active) return;
    const t = setTimeout(() => {
      docsApi.update(active.id, { kind: KIND, title: active.title, content: active.content }).then(d => {
        setDocs(ds => ds.map(x => x.id === d.id ? d : x));
      });
    }, 700);
    return () => clearTimeout(t);
  }, [active?.title, active?.content]);

  const create = async () => {
    const d = await docsApi.create({ kind: KIND, title: "Untitled Document", content: "" });
    setDocs([d, ...docs]);
    setActive(d);
  };

  const runAI = async (mode) => {
    if (!active) return;
    setAiBusy(true);
    const map = {
      summarize: `Summarize the following document briefly:\n\n${active.content}`,
      rewrite: `Rewrite the document to be more professional and well-structured:\n\n${active.content}`,
      expand: `Expand and elaborate on the document:\n\n${active.content}`,
      custom: `${aiPrompt}\n\nDocument:\n${active.content}`,
    };
    try {
      const r = await aiQuick(map[mode], "You are a professional writing assistant. Return only the result text.");
      setActive({ ...active, content: r.text });
    } catch {}
    setAiBusy(false);
    setAiPrompt("");
  };

  const fmt = (cmd) => document.execCommand(cmd, false, null);

  return (
    <div className="h-full flex flex-col md:flex-row" data-testid="app-word">
      <div className="w-full md:w-56 border-b md:border-b-0 md:border-r border-white/10 flex flex-col md:h-auto max-h-40 md:max-h-none">
        <div className="p-2 flex items-center justify-between">
          <span className="text-xs uppercase tracking-widest text-white/40">Documents</span>
          <button data-testid="word-new" onClick={create} className="btn-ghost p-1.5"><Plus size={12} /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-2 flex md:block gap-1">
          {docs.map(d => (
            <button key={d.id} onClick={() => setActive(d)} className={`w-full text-left px-3 py-2 rounded mb-1 text-sm whitespace-nowrap md:whitespace-normal ${active?.id === d.id ? "bg-white/10" : "hover:bg-white/5"}`}>
              <FileText size={12} className="inline mr-1.5 text-white/50" />
              {d.title}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {active ? (
          <>
            <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
              <input
                data-testid="word-title"
                value={active.title}
                onChange={(e) => setActive({ ...active, title: e.target.value })}
                className="bg-transparent outline-none text-sm font-medium"
              />
              <div className="flex items-center gap-1">
                <button onClick={() => fmt("bold")} className="btn-ghost p-1.5"><Bold size={12} /></button>
                <button onClick={() => fmt("italic")} className="btn-ghost p-1.5"><Italic size={12} /></button>
                <button onClick={() => fmt("underline")} className="btn-ghost p-1.5"><Underline size={12} /></button>
              </div>
            </div>

            <div className="flex-1 flex flex-col lg:flex-row min-h-0">
              <div
                contentEditable
                data-testid="word-editor"
                suppressContentEditableWarning
                onBlur={(e) => setActive({ ...active, content: e.currentTarget.innerHTML })}
                dangerouslySetInnerHTML={{ __html: active.content || "<p>Start writing your document...</p>" }}
                className="flex-1 p-4 sm:p-8 outline-none overflow-y-auto text-[14px] leading-relaxed bg-white/2"
                style={{ maxWidth: "100%" }}
              />

              <div className="w-full lg:w-64 border-t lg:border-t-0 lg:border-l border-white/10 p-3 flex flex-col gap-2 bg-black/30">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-white/40">
                  <Sparkles size={12} color="#ff7a2e" /> AI Assistant
                </div>
                <button data-testid="word-ai-summarize" disabled={aiBusy} onClick={() => runAI("summarize")} className="btn-ghost text-xs justify-start text-left flex items-center gap-1">
                  {aiBusy ? <Loader2 size={12} className="animate-spin" /> : null} Summarize
                </button>
                <button data-testid="word-ai-rewrite" disabled={aiBusy} onClick={() => runAI("rewrite")} className="btn-ghost text-xs">Rewrite Professionally</button>
                <button data-testid="word-ai-expand" disabled={aiBusy} onClick={() => runAI("expand")} className="btn-ghost text-xs">Expand</button>
                <div className="mt-2 text-[10px] uppercase tracking-widest text-white/40">Custom Prompt</div>
                <textarea
                  data-testid="word-ai-prompt"
                  rows={3}
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder="e.g. translate to French"
                  className="bg-white/5 border border-white/10 rounded p-2 text-xs"
                />
                <button data-testid="word-ai-run" disabled={aiBusy || !aiPrompt} onClick={() => runAI("custom")} className="btn-accent text-xs">Run</button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-white/40 text-sm">Create a document</div>
        )}
      </div>
    </div>
  );
}
